import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface ArticleCardProps {
  headline: string;
  excerpt: string;
  cover?: string;
  tag?: string;
  readingTime?: number;
  writer?: string;
  publishedAt?: Date;
  clampLines?: number;
  href?: string;
}

export function formatReadTime(seconds: number): string {
  if (!seconds || seconds < 60) return 'Less than 1 min read';
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} min read`;
}

export function formatPostDate(date: Date): string {
  if (!date) return '';
  return date.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function ArticleCard({
  cover,
  tag,
  readingTime,
  headline,
  excerpt,
  writer,
  publishedAt,
  clampLines = 3,
  href,
}: ArticleCardProps) {
  const hasMeta = tag || readingTime;
  const hasFooter = writer || publishedAt;

  const inner = (
    <Card className="flex h-full w-full max-w-none flex-col gap-3 overflow-hidden rounded-3xl border border-neutral-200 p-3 shadow-lg transition-shadow hover:shadow-xl">
      {cover && (
        <CardHeader className="p-0">
          <div className="relative h-56 w-full overflow-hidden rounded-2xl">
            <img src={cover} alt={headline} className="h-full w-full object-cover" loading="lazy" />
          </div>
        </CardHeader>
      )}

      <CardContent className="flex-grow p-3">
        {hasMeta && (
          <div className="mb-4 flex items-center text-sm text-neutral-500">
            {tag && (
              <Badge className="rounded-full bg-neutral-100 px-3 py-1 text-sm font-medium text-neutral-600 hover:bg-neutral-200">
                {tag}
              </Badge>
            )}
            {tag && readingTime && <span className="mx-2">•</span>}
            {readingTime && <span>{formatReadTime(readingTime)}</span>}
          </div>
        )}

        <h2 className="mb-2 text-2xl font-bold leading-tight text-neutral-900">{headline}</h2>

        <p
          className={cn('text-neutral-600', {
            'overflow-hidden text-ellipsis [-webkit-box-orient:vertical] [display:-webkit-box]':
              clampLines && clampLines > 0,
          })}
          style={{ WebkitLineClamp: clampLines }}
        >
          {excerpt}
        </p>
      </CardContent>

      {hasFooter && (
        <CardFooter className="flex items-center justify-between p-3">
          {writer && (
            <div>
              <p className="text-sm text-neutral-500">By</p>
              <p className="font-semibold text-neutral-700">{writer}</p>
            </div>
          )}
          {publishedAt && (
            <div className={writer ? 'text-right' : ''}>
              <p className="text-sm text-neutral-500">Published</p>
              <p className="font-semibold text-neutral-700">{formatPostDate(publishedAt)}</p>
            </div>
          )}
        </CardFooter>
      )}
    </Card>
  );

  if (href) {
    return (
      <a href={href} className="block h-full w-full transition-transform hover:-translate-y-1">
        {inner}
      </a>
    );
  }

  return inner;
}
